import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Container, Grid, Header, Segment, Icon, Statistic, Image, Button, Divider } from 'semantic-ui-react';
import { AuthStdFooter } from "../../utils/auth-std-footer";
import { getMenuIdForFeature } from '../../utils/roleUtils';
import BookManagementBc from '../../bookWorksheet/components/management/BookManagement-bc';
import { fetchStatistics, selectStatisticsByView, selectStatisticsLoading } from '../../slices/statisticsSlice';
import { STAT_SETS } from '../../constants/statisticsFlags';
import useIsLoggedIn from '../../isLoggedIn';
import '../../styles/landing-pages.css';
import '../../styles/article.css';
import bookStack from '../../assets/justBookStack.svg'
import hourGlass from '../../assets/hourGlassLine.svg'
import checkBox from '../../assets/checkListLine.svg'
import myLibrary from '../../assets/myLibrary.svg'
import profileIcon from '../../assets/profileLineGlasses.svg'
import myProfileLine from '../../assets/myProfileLine.svg'
import lightening from '../../assets/lightening.svg'

const AddBookLandingPage = () => {
  const dispatch = useDispatch();
  const authToken = useIsLoggedIn();
  const userProfile = useSelector((state) => state.user.profile);
  const userName = userProfile?.data?.title || 'Book Creator';

  // Get statistics from Redux using the new statistics system
  const bookPortfolioStats = useSelector(selectStatisticsByView('book_portfolio'));
  const isLoadingStats = useSelector(selectStatisticsLoading('book_portfolio'));

  // Initialize page data
  useEffect(() => {
    if (authToken) {
      // Fetch statistics for book portfolio
      dispatch(fetchStatistics({
        viewType: 'book_portfolio',
        flags: STAT_SETS.BOOK_PORTFOLIO
      }));
    }
  }, [dispatch, authToken]);

  // Statistics for the overview section - using new statistics system
  const statistics = [
    {
      label: 'Literary Groups',
      value: isLoadingStats ? '...' : (bookPortfolioStats?.litGroupCount || '0'),
      icon: 'users',
      img: myLibrary,
      link: '/add-literary-group-landing'
    },
    {
      label: 'Total Books',
      value: isLoadingStats ? '...' : (bookPortfolioStats?.bookCount || '0'),
      icon: 'book',
      img: bookStack,
      link: '/dashboard',
      state: { menuSelection: getMenuIdForFeature('bookDetails') }
    },
    {
      label: 'In Progress',
      value: isLoadingStats ? '...' : (bookPortfolioStats?.inProgressCount || '0'),
      icon: 'hourglass half',
      img: hourGlass,
      link: '/dashboard',
      state: { menuSelection: getMenuIdForFeature('bookDetails') }
    },
    {
      label: 'Completed',
      value: isLoadingStats ? '...' : (bookPortfolioStats?.completedCount || '0'),
      icon: 'check circle',
      img: checkBox,
      link: '/dashboard',
      state: { menuSelection: getMenuIdForFeature('bookDetails') }
    }
    // ,
    // {
    //   label: 'My Profile',
    //   value: '',
    //   icon: 'user',
    //   img: profileIcon,
    //   link: '/profile'
    // }
  ];

  return (
    <Container fluid className="landing-page book-creator-landing">
      <Container style={{ marginTop: "30px" }}>
        <Grid className="articleListWidth">
          <Grid.Row>
            <Grid.Column width={3}>
              <div className="img-with-gradient">
                <Image src="/static/media/shutterstock_2294396055-1024x1024.2d3b550af78ebc4d2e7b.jpg" />
              </div>
            </Grid.Column>
            <Grid.Column width={13} textAlign="center">
              <span className="blgListItemTitle">Welcome, {userName}</span>
              <span className="blgListSubTitle">Book Management</span>
              <p className="landing-subheading-text">
                Create and manage your books within your literary groups from this centralized hub.
                Select a literary group to view its books, create new books, edit existing ones,
                and organize your creative works efficiently.
              </p>
              <div style={{ textAlign: 'left', marginTop: '1em' }}>
                <Button
                  as={Link}
                  to="/dashboard"
                  size="large"
                  className="landing-goto-button"
                >
                  Go to Main Dashboard
                </Button>
              </div>
            </Grid.Column>
          </Grid.Row>
        </Grid>
        <Grid className="articleListWidth" style={{ marginTop: "20px", borderTop: "solid 1px #AAAAAA" }}></Grid>
      </Container>

      <Container style={{ marginTop: '2em', marginBottom: '2em' }}>
        <Grid>
          <Grid.Row>
            <Grid.Column width={6}>
              <Header as="h2">
                <img src={myProfileLine} alt="" />
                <Header.Content style={{
                  fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
                  fontWeight: '400',
                  fontSize: '2.5rem'
                }}>
                  Your Book Portfolio
                  <Header.Subheader>Overview of your books and literary works</Header.Subheader>
                </Header.Content>
              </Header>
            </Grid.Column>
            <Grid.Column width={10}>
              <div>
                <Statistic.Group widths="five">
                  {statistics.map((stat, index) => {
                    const StatisticContent = (
                      <>
                        <Statistic.Value>
                          <div style={{
                            fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
                            fontWeight: '300',
                            fontSize: '3.0rem'
                          }}><img src={stat.img} alt="" /> {stat.value}</div>
                        </Statistic.Value>
                        <Statistic.Label style={{
                          fontFamily: 'Inter, system-ui, Avenir, Helvetica, Arial, sans-serif',
                          fontWeight: '300',
                          fontSize: '1.1rem'
                        }}>{stat.label}</Statistic.Label>
                      </>
                    );

                    if (stat.link) {
                      return (
                        <Statistic
                          key={index}
                          as={Link}
                          to={stat.link}
                          state={stat.state}
                          className="statistic-link"
                        >
                          {StatisticContent}
                        </Statistic>
                      );
                    }

                    return (
                      <Statistic key={index}>
                        {StatisticContent}
                      </Statistic>
                    );
                  })}
                </Statistic.Group>
              </div>
            </Grid.Column>
          </Grid.Row>
        </Grid>
        <Divider />

        <Header as="h2" dividing style={{ marginTop: '2em' }}>
          <img src={lightening} alt="" />
          <Header.Content>
            Book Management
            <Header.Subheader>Select a literary group to manage its books</Header.Subheader>
          </Header.Content>
        </Header>

        {/* Main Book Management Component */}
        <div style={{ marginTop: '2em' }}>
          <BookManagementBc />
        </div>

        <Grid columns={2} stackable style={{ marginTop: '3em' }}>
          <Grid.Column>
            <Header as="h2" dividing>
              <img src={myLibrary} alt="" style={{ height: '50px' }} />
              <Header.Content>
                Book Organization Tips
                <Header.Subheader>Best practices for managing your books</Header.Subheader>
              </Header.Content>
            </Header>

            <Segment>
              <div style={{ marginBottom: '1em' }}>
                <Header as="h4">
                  <Icon name="lightbulb" color="yellow" />
                  <Header.Content>Pro Tips</Header.Content>
                </Header>
                <p>• Use descriptive titles that clearly identify your book's content</p>
                <p>• Add detailed descriptions to help with organization</p>
                <p>• Consider creating books for different story arcs or volumes</p>
                <p>• Keep book information updated as your story develops</p>
              </div>
              <div style={{ textAlign: 'center', marginTop: '1em' }}>
                <Button
                  as={Link}
                  to="/add-literary-group-landing"
                  compact
                  primary
                >
                  Manage Literary Groups
                </Button>
              </div>
            </Segment>
          </Grid.Column>

          <Grid.Column>
            <Header as="h2" dividing>
              <img src={bookStack} alt="" style={{ height: '50px' }} />
              <Header.Content>
                Next Steps
                <Header.Subheader>After creating your books</Header.Subheader>
              </Header.Content>
            </Header>

            <Segment>
              <div style={{ marginBottom: '1em' }}>
                <Header as="h4">
                  <Icon name="arrow right" color="blue" />
                  <Header.Content>What's Next?</Header.Content>
                </Header>
                <p>• Add characters to your books and define their personalities</p>
                <p>• Import existing manuscripts and story content</p>
                <p>• Create voice variations for different character moods</p>
                <p>• Set up voice actor assignments for character dialogue</p>
              </div>
              <div style={{ textAlign: 'center', marginTop: '1em' }}>
                <Button
                  as={Link}
                  to="/dashboard"
                  state={{ menuSelection: getMenuIdForFeature('storyImporter') }}
                  compact
                  color="blue"
                >
                  Import Stories
                </Button>
              </div>
            </Segment>
          </Grid.Column>
        </Grid>
      </Container>

      {AuthStdFooter()}
    </Container>
  );
};

export default AddBookLandingPage;
